import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, MapPin, ArrowLeft, Loader2 } from 'lucide-react';
import { PackageCard } from '../components/PackageCard';
import { FiltersPanel } from '../components/FiltersPanel';
import { getPackages } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default images for countries
const DEFAULT_IMAGES = {
  'Turcia': 'https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?w=600',
  'Italia': 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=600',
  'Spania': 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=600',
  'Franța': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600',
  'Grecia': 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=600',
  'Portugalia': 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=600',
  'Austria': 'https://images.unsplash.com/photo-1609856878074-cf31e21ccb6b?w=600',
  'Marea Britanie': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=600',
  'default': 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=600'
};

// Helper to create URL-safe slug
const createSlug = (name) => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Country card component
const CountryCard = ({ country }) => {
  const slug = createSlug(country.name);
  const image = country.image_url || DEFAULT_IMAGES[country.name] || DEFAULT_IMAGES.default;
  
  return (
    <Link
      to={`/city-break/${slug}`}
      className="group block relative h-48 md:h-56 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_8px_rgba(212,175,55,0.2)]"
      data-testid={`citybreak-country-card-${slug}`}
    >
      <img
        src={image}
        alt={country.name}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent z-10" />
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-end p-4 text-center">
        <MapPin className="w-6 h-6 text-gold mb-2" />
        <h4 className="font-heading font-semibold text-lg text-white">{country.name}</h4>
        <p className="text-white/60 text-xs mt-1 line-clamp-2">
          {country.description || `City Break-uri în ${country.name}`}
        </p>
        <div className="mt-2 flex items-center gap-1 text-gold text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          <span>Vezi oferte</span>
          <ChevronRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
};

export default function CityBreakPage() {
  const { country } = useParams();
  
  // Countries from API
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  
  // Package data
  const [packages, setPackages] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: null, max: null });
  const [selectedTransport, setSelectedTransport] = useState([]);
  const [sortOrder, setSortOrder] = useState('none');
  const [loading, setLoading] = useState(false);

  // Helper function to parse price
  const parsePrice = (price) => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      const match = price.replace(/\./g, '').match(/[\d]+/);
      return match ? parseInt(match[0]) : 0;
    }
    return 0;
  };

  // Load countries from API
  useEffect(() => {
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await fetch(`${API_URL}/api/countries?category=City%20Break`);
        const data = await response.json();
        setCountries(data);
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    
    loadCountries();
  }, []);

  // Find current country based on URL param
  const currentCountry = useMemo(() => {
    if (!country) return null;
    return countries.find(c => createSlug(c.name) === country);
  }, [country, countries]);

  // Load packages when viewing a specific country
  useEffect(() => {
    if (currentCountry) {
      const loadPackages = async () => {
        setLoading(true);
        try {
          const pkgs = await getPackages({ category: 'City Break' });
          
          // Filter by country name
          const countryName = currentCountry.name;
          const filtered = pkgs.filter(pkg => pkg.country === countryName);
          
          setPackages(filtered);
          
          // Calculate operators count
          const operatorCounts = {};
          filtered.forEach(pkg => {
            if (pkg.operator) {
              operatorCounts[pkg.operator] = (operatorCounts[pkg.operator] || 0) + 1;
            }
          });
          
          const countryOperators = Object.entries(operatorCounts).map(([name, count]) => ({
            name,
            count
          }));
          
          setOperators(countryOperators);
          
          // Set initial price range
          if (filtered.length > 0) {
            const prices = filtered.map(p => parsePrice(p.price)).filter(p => p > 0);
            if (prices.length > 0) {
              setPriceRange({
                min: Math.floor(Math.min(...prices)),
                max: Math.ceil(Math.max(...prices))
              });
            }
          }
        } catch (error) {
          console.error('Error loading packages:', error);
        } finally {
          setLoading(false);
        }
      };
      
      loadPackages();
    } else {
      setPackages([]);
    }
  }, [currentCountry]);

  // Apply filters
  const filteredPackages = useMemo(() => {
    let result = [...packages];
    
    // Filter by operators
    if (selectedOperators.length > 0) {
      result = result.filter(pkg => selectedOperators.includes(pkg.operator));
    }
    
    // Filter by price range
    if (priceRange.min !== null) {
      result = result.filter(pkg => parsePrice(pkg.price) >= priceRange.min);
    }
    if (priceRange.max !== null) {
      result = result.filter(pkg => parsePrice(pkg.price) <= priceRange.max);
    }
    
    // Filter by transport
    if (selectedTransport.length > 0) {
      result = result.filter(pkg => {
        const transport = (pkg.transport_type || 'avion').toLowerCase();
        return selectedTransport.some(t => t.toLowerCase() === transport);
      });
    }
    
    // Sort
    if (sortOrder === 'asc') {
      result.sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
    } else if (sortOrder === 'desc') {
      result.sort((a, b) => parsePrice(b.price) - parsePrice(a.price));
    }
    
    return result;
  }, [packages, selectedOperators, priceRange, selectedTransport, sortOrder]);

  // Reset filters handler
  const handleResetFilters = () => {
    setSelectedOperators([]);
    setSelectedTransport([]);
    setSortOrder('none');
    if (packages.length > 0) {
      const prices = packages.map(p => parsePrice(p.price)).filter(p => p > 0);
      if (prices.length > 0) {
        setPriceRange({
          min: Math.floor(Math.min(...prices)),
          max: Math.ceil(Math.max(...prices))
        });
      }
    }
  };

  // Loading state
  if (loadingCountries) {
    return (
      <div className="min-h-screen bg-navy pt-20 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-gold animate-spin" />
      </div>
    );
  }

  // Show packages for a specific country
  if (currentCountry) {
    return (
      <div className="min-h-screen bg-navy pt-20" data-testid="citybreak-country-page">
        {/* Header */}
        <div className="bg-gradient-to-b from-navy-light to-navy py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-4">
              <Link to="/city-break" className="text-white/60 hover:text-gold transition-colors">
                City Break
              </Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <span className="text-gold">{currentCountry.name}</span>
            </div>
            
            {/* Back button */}
            <Link
              to="/city-break"
              className="inline-flex items-center gap-2 text-white/60 hover:text-gold transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Toate destinațiile
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gold/20 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-gold" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">
                  City Break {currentCountry.name}
                </h1>
                <p className="text-white/60 mt-1">
                  {filteredPackages.length} oferte disponibile
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-12 h-12 text-gold animate-spin" />
            </div>
          ) : packages.length > 0 ? (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Filters - always show when there are packages in this country */}
              <div className="lg:w-72 flex-shrink-0">
                <FiltersPanel
                  packages={packages}
                  operators={operators}
                  selectedOperators={selectedOperators}
                  onOperatorChange={setSelectedOperators}
                  priceRange={priceRange}
                  onPriceChange={setPriceRange}
                  selectedTransport={selectedTransport}
                  onTransportChange={setSelectedTransport}
                  sortOrder={sortOrder}
                  onSortChange={setSortOrder}
                  onReset={handleResetFilters}
                />
              </div>
              
              {/* Packages Grid or Empty State */}
              <div className="flex-1">
                {filteredPackages.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredPackages.map((pkg) => (
                      <PackageCard key={pkg.id} package={pkg} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 glass rounded-2xl">
                    <p className="text-white/60 text-lg mb-2">
                      Nu există oferte care să corespundă filtrelor selectate.
                    </p>
                    <button
                      onClick={handleResetFilters}
                      className="text-gold hover:underline mt-2"
                    >
                      Resetează filtrele
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-white/60 text-lg">
                Nu există oferte City Break pentru {currentCountry.name} momentan.
              </p>
              <Link
                to="/city-break"
                className="inline-flex items-center gap-2 text-gold hover:underline mt-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Explorează alte destinații
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show all countries for City Break
  return (
    <div className="min-h-screen bg-navy pt-20" data-testid="citybreak-page">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-navy-light to-navy py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">
            City Break
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Escapade urbane de 2-5 zile în cele mai frumoase orașe europene
          </p>
        </div>
      </div>

      {/* Countries Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-heading font-semibold text-xl text-white mb-6">
          Alege o destinație ({countries.length} disponibile)
        </h2>
        {countries.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {countries.map((c) => (
              <CountryCard key={c.id} country={c} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-white/60 text-lg">
              Nu există destinații configurate momentan.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
